import { useState } from 'react';
import { X, UserPlus, Info } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { Card, Badge, cx } from './ui';

// Team & roles (PRD §2.5: owner | admin | view_only | developer). Admins manage who can access
// Pavlov. There is NO per-seat charge — only platform usage (Simulation Units) is billed.
interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  last_login_at: string | null;
}
const ROLES = ['owner', 'admin', 'view_only', 'developer'];
const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  view_only: 'View only',
  developer: 'Developer',
};

export function UsersPanel({
  onClose,
  currentEmail,
}: {
  onClose: () => void;
  currentEmail?: string;
}) {
  const { data, error, reload } = useLoad(() => api.get<{ data: User[] }>('/v1/users'));
  const users = data?.data ?? [];
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function changeRole(id: string, newRole: string) {
    setMsg(null);
    try {
      await api.patch(`/v1/users/${id}`, { role: newRole });
      reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function invite() {
    if (!email.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/v1/users', { email: email.trim(), name: name.trim() || undefined, role });
      setEmail('');
      setName('');
      setMsg('Invite added — they can sign in with that email.');
      reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Invite failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-ink/20" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col overflow-auto border-l border-line bg-paper">
        <div className="flex items-center justify-between border-b border-line px-6 py-5">
          <div className="font-display text-xl text-ink">Team &amp; roles</div>
          <button onClick={onClose} className="text-muted hover:text-ink" title="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="flex items-start gap-2 rounded-input border border-line bg-ink/[0.02] px-4 py-3 text-sm text-muted">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            Admins &amp; platform users are free — you’re only billed for platform usage (Simulation
            Units). Invite as many teammates as you need.
          </div>

          {(error || msg) && (
            <div className="rounded-input border border-line bg-ink/[0.03] px-3 py-2 text-sm text-ink">
              {error ?? msg}
            </div>
          )}

          {/* Invite */}
          <div>
            <div className="eyebrow mb-2">Invite a teammate</div>
            <Card className="space-y-3 p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="input"
                />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="input"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="input w-44 cursor-pointer"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => void invite()}
                  disabled={busy || !email}
                  className="btn-primary"
                >
                  <UserPlus className="h-4 w-4" /> Invite
                </button>
              </div>
            </Card>
          </div>

          {/* Members */}
          <div>
            <div className="eyebrow mb-2">Members</div>
            <Card className="divide-y divide-line">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line text-xs font-medium text-muted">
                    {(u.name || u.email).slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 truncate text-sm font-medium text-ink">
                      {u.name}
                      {u.email === currentEmail && <Badge tone="muted">you</Badge>}
                    </div>
                    <div className="truncate text-xs text-muted">{u.email}</div>
                  </div>
                  <select
                    value={u.role}
                    onChange={(e) => void changeRole(u.id, e.target.value)}
                    className={cx('input w-36 cursor-pointer py-1.5 text-xs')}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
              {users.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-muted">No teammates yet.</div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
